import React, { useId } from 'react'
import { useEffect, useState } from 'react'
import { Alert, Form, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import apiClient from '../../utils/api/apiClient'
import { APIEndpoints } from '../../utils/api/apiConfig'
import { useCancelToken } from '../../utils/hooks/useCancelToken'
import SearchField from '../Forms/SearchField'
import BP from './BP'
import LazyLoad from '../Forms/LazyLoad'
import SearchResultAmount from '../Forms/SearchResultAmount'
import { makeCall } from '../../utils/api/makeCall'
import useMountEffect from '../../utils/hooks/useMountEffect'

const BPSelect = ({ selectedBPs, setSelectedBPs, hideBPs, listRef, singleSelection}) => {

  const [fetchedBPs, setFetchedBPs] = useState([])
  const [searchText, setSearchText] = useState('')
  const [offset, setOffset] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const { t } = useTranslation()
  const { createOrCancelToken, sourceRef, isCancel } = useCancelToken();
  const limit = 10
  const [lastClickedBPId, setLastClickedBPId] = useState(null)
  const [allowRange, setAllowRange] = useState(false);
  const [allSelected, setAllSelected] = useState(false)
  const filteredArray = hideBPs ? fetchedBPs.filter(item => !hideBPs.find(element => element === item.id)) : fetchedBPs
  const [, setErrorStates] = useState({})
  const [loadingStates, setLoadingStates] = useState({})
  const handleChangeSearchText = async (newSearchText) => {
    setSearchText(newSearchText)
    fetchBPs({ newOffset: 0, newSearch: newSearchText })
  }

  const fetchBPs = async (newParams = { newOffset: offset, newSearch: searchText }) => {
    createOrCancelToken()
    let append = !!newParams.newOffset
    const params = getParamsForBPsRequest(newParams)
    makeCall('getBPs', async () => {
      let response = await apiClient.getJson(APIEndpoints.partialBusinessPartners, params, true, true, true, { cancelToken: sourceRef.current.token })
      setFetchedBPs(prev => append ? [...prev, ...response.data] : response.data)
      setTotalCount(response.headers['x-total-count'] ? parseInt(response.headers['x-total-count']) : 0)
      setOffset(newParams.newOffset + limit)
    }, setErrorStates, setLoadingStates, isCancel)
  }

  const checkSessionStorage = async () => {
    if (sessionStorage.getItem('searchBPNumber')) {
      let bpNumber = JSON.parse(sessionStorage.getItem('searchBPNumber'));
      sessionStorage.removeItem('searchBPNumber')
      await handleChangeSearchText(bpNumber)
      selectAll()
    }
  }
  useMountEffect(() => {
    checkSessionStorage()
  })

  const getParamsForBPsRequest = (newParams) => {
    const { newOffset, newSearch } = newParams
    return ({
      'filter.searchText': newSearch ? newSearch : null,
      'sort':'displayName',
      'page.offset': newOffset,
      'page.limit': limit,
    })
  }

  function getBPsInRange(lastClickedId, newId) {
    let lastIndex = filteredArray.findIndex(bp => bp.id === lastClickedId)
    let newIndex = filteredArray.findIndex(bp => bp.id === newId)
    if (lastIndex < newIndex) {
      return filteredArray.slice(lastIndex, newIndex + 1)
    }
    else if (lastIndex > newIndex) {
      return filteredArray.slice(newIndex, lastIndex + 1)
    }
    else return []
  }

  const onClickSelectedBPs = (bp) => {
    setAllSelected(false)
    let selectedBPId = bp.id
    setLastClickedBPId(selectedBPId)
    let newSelectedBPs = [...selectedBPs]
    let existingBPIndex = selectedBPs.findIndex(bp => bp.id === selectedBPId)
    if (allowRange) {
      let bpsInRange = getBPsInRange(lastClickedBPId, selectedBPId)
      //lastClickedBPId is selected atm
      if (selectedBPs.find(bp => bp.id === lastClickedBPId)) {
        //add bps
        setSelectedBPs([...new Set([...newSelectedBPs, ...bpsInRange])])
      }
    }
    else if (existingBPIndex >= 0) {
      //remove bp if already selected
      newSelectedBPs.splice(existingBPIndex, 1)
      setSelectedBPs(newSelectedBPs)
    } else {
      if (singleSelection) newSelectedBPs = []
      newSelectedBPs.push(bp)
      setSelectedBPs(newSelectedBPs)
    }
  }

  useEffect(() => {
    let listRefCurrent = listRef?.current
    const keypressShiftCallback = e => {
      if (!singleSelection && (e.shiftKey && lastClickedBPId)) {
        setAllowRange(true);
      } else {
        setAllowRange(false);
      }
    }
    if (listRefCurrent) {
      listRefCurrent.addEventListener('click', keypressShiftCallback)
    }
    return () => { listRefCurrent && listRefCurrent.removeEventListener('click', keypressShiftCallback); }
  }, [lastClickedBPId, listRef, singleSelection])

  const setAllBPs = async () => {
    var bps = []
    const params = {
      'filter.searchText': searchText,
      'sort':'displayName'
    }
    await makeCall('getAllBPs', async () => {
        bps = await apiClient.getJson(APIEndpoints.partialBusinessPartners, params)
    }, setErrorStates, setLoadingStates, isCancel)
    return bps
  }

  const selectAll = async () => {
    var bps = []
    if (!allSelected) {
        bps = await setAllBPs()
    }
    setSelectedBPs(bps)
    setAllSelected(prev => !prev)
  }
  const checkboxId = useId()
  return (
    <>
      <Form.Group className="mb-3 p-2">
        <Form.Label>{t('searchBP:searchBP')}</Form.Label>
        <SearchField
          placeholder={t('searchBP:searchBP')}
          value={searchText}
          onChange={e => handleChangeSearchText(e.target.value)}
          onClearClick={() => handleChangeSearchText('')}
        />
        <div className=" d-flex justify-content-between">
          {searchText ? <SearchResultAmount count={totalCount} /> : <div className="mt-2">&nbsp;</div>}
          {loadingStates.getAllBPs
            ? <Spinner size="sm" className="mt-2" animation='border' variant='primary' />
            : filteredArray.length && !singleSelection ? <Form.Check
              className="mt-2"
              label={t('order:selectAll')}
              id={checkboxId}
              checked={allSelected}
              onChange={selectAll}
            />
              : null}
        </div>

      </Form.Group >
      {filteredArray.map((bp, index) => (
          <BP key={bp.id} onClickSelectedBPs={onClickSelectedBPs} selectedBPs={selectedBPs} bp={bp} index={index} />
      ))}
      {
        (searchText && !loadingStates.getBPs && !filteredArray.length) &&
        <>
          <Alert variant='info' >{t('searchBP:noBPsAvailable')}</Alert>
        </>
      }
      {((totalCount > offset) && !loadingStates.getBPs) ?
        (<LazyLoad
          fetchData={fetchBPs}
          listRef={listRef}
        />) : ''}
      {loadingStates.getBPs &&
        <div className='d-flex justify-content-center p-3' >
          <Spinner animation='border' variant='primary' />
        </div>}
    </>
  )
}

export default BPSelect